import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {
  baseUrlV1,
  baseUrlV2,
  billingServiceBaseUrl,
  consultationService,
  offlineAudioServiceBaseUrl,
} from '../configs/api/api.config';
import { RootState } from '../store/store';
import { logout, setCredentials, setUserData } from '../store/auth.slice';

export interface IResponseType<T> {
  '@context': string;
  '@id': string;
  '@type': string;
  'hydra:member': T[];
  'hydra:totalItems': number;
}

const nonEditableEndpoints = ['changeAvatar', 'uploadCloudsImage'];

const prepareAuthHeaders = (headers: Headers, getState: () => unknown) => {
  const token = (getState() as RootState).authSlice.token;
  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }
  return headers;
};

const createBaseQueryV2 = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => prepareAuthHeaders(headers, getState),
  });

export const baseQueryV2 = createBaseQueryV2(baseUrlV2);
export const baseQueryBilling = createBaseQueryV2(billingServiceBaseUrl);
export const baseQueryOfflineAudio = createBaseQueryV2(offlineAudioServiceBaseUrl);

export const baseQuery = fetchBaseQuery({
  baseUrl: baseUrlV1,
  prepareHeaders: (headers, { getState, type, endpoint }) => {
    const token = (getState() as RootState).authSlice.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    if (nonEditableEndpoints.includes(endpoint)) return headers;
    if (type === 'query') {
      headers.set('Content-Type', 'application/ld+json');
    }
    if (type === 'mutation') {
      headers.set('Content-Type', 'application/ld+json');
    }

    return headers;
  },
});
export const baseQueryWithoutHeaders = fetchBaseQuery({
  baseUrl: baseUrlV1,
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshToken = (api.getState() as RootState).authSlice.refreshToken;

    try {
      const refreshResult: any = await baseQueryWithoutHeaders(
        {
          url: 'token/refresh',
          method: 'POST',
          body: { refresh_token: refreshToken },
        },
        api,
        extraOptions,
      );
      const me: any = await baseQuery(
        {
          url: '/users/me',
          method: 'GET',
        },
        api,
        extraOptions,
      );

      api.dispatch(setUserData(me.data));

      if (refreshResult?.data) {
        api.dispatch(setCredentials({ ...refreshResult.data }));
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(logout());
      }
    } catch (e) {
      console.log(e);
      api.dispatch(logout());
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['user'],
  endpoints: (build) => ({}),
});

/**
 * Маркер «гостевого» запроса к consultation-service: эндпоинт помечает им запрос,
 * который должен уйти без Authorization, даже если пользователь залогинен.
 * Наружу не отправляется — удаляется в prepareHeaders.
 */
export const GUEST_REQUEST_HEADER = 'x-zingizi-guest-request';

const isGuestRequest = (headers: Headers) => headers.has(GUEST_REQUEST_HEADER);

const baseQueryConsultationService = fetchBaseQuery({
  baseUrl: consultationService,
  prepareHeaders: (headers, { getState, type }) => {
    const token = (getState() as RootState).authSlice.token;

    if (isGuestRequest(headers)) {
      headers.delete(GUEST_REQUEST_HEADER);
      headers.delete('authorization');
    } else if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    if (type === 'mutation') {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});

/** Гостевой запрос уходит без токена, поэтому 401 по нему нельзя лечить refresh'ем (и тем более logout'ом). */
const isGuestArgs = (args: string | FetchArgs) => {
  if (typeof args === 'string' || !args.headers) {
    return false;
  }

  return new Headers(args.headers as HeadersInit).has(GUEST_REQUEST_HEADER);
};

export const baseQueryConsultationServiceWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQueryConsultationService(args, api, extraOptions);

  if (result.error && result.error.status === 401 && !isGuestArgs(args)) {
    const refreshToken = (api.getState() as RootState).authSlice.refreshToken;

    try {
      const refreshResult: any = await baseQueryWithoutHeaders(
        {
          url: 'token/refresh',
          method: 'POST',
          body: { refresh_token: refreshToken },
        },
        api,
        extraOptions,
      );

      if (refreshResult?.data) {
        api.dispatch(setCredentials({ ...refreshResult.data }));
        result = await baseQueryConsultationService(args, api, extraOptions);
      } else {
        api.dispatch(logout());
      }
    } catch (e) {
      console.log(e);
      api.dispatch(logout());
    }
  }

  return result;
};
